import { useEffect, useState } from "react";
import axios from "axios";
import { fmtError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Loader2, Copy, Check, KeyRound, CalendarDays, AlertCircle } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IntegrationsPage() {
  const { slug } = useTenant();
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${BASE}/tenants/${slug}/info`, { withCredentials: true })
      .then((r) => setInfo(r.data))
      .catch((e) => setError(fmtError(e)));
  }, [slug]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { console.error("clipboard copy failed", e); }
  };

  if (!info) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#A78BFA]" /></div>;

  return (
    <div data-testid="integrations-page" className="max-w-4xl">
      <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-3">Integrazioni</div>
      <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#1A1A1A]">
        Collega il <span className="italic">tuo mondo.</span>
      </h1>
      <p className="mt-4 text-[#4A4A4A] font-light max-w-2xl">
        Sincronizza il tuo CRM desktop con AURA: agenda e dati clienti rimangono allineati ovunque.
      </p>

      {error && <div className="mt-4 text-sm text-[#B91C1C]">{error}</div>}

      {/* API Key */}
      <div className="mt-10 rounded-3xl border border-black/5 bg-white p-8" data-testid="api-key-card">
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-[#1A1A1A] to-[#2A1F3D] text-white flex items-center justify-center">
            <KeyRound size={20} />
          </div>
          <div className="flex-1">
            <h2 className="font-serif text-2xl text-[#1A1A1A]">API Key per CRM desktop</h2>
            <p className="mt-2 text-sm text-[#4A4A4A] font-light leading-relaxed">
              Usa questa chiave nel connettore del tuo CRM desktop AURA per inviare appuntamenti, clienti e trattamenti automaticamente.
              Endpoint: <code className="text-xs bg-[#FAFAFA] px-1.5 py-0.5 rounded">/api/sync/...</code> con header <code className="text-xs bg-[#FAFAFA] px-1.5 py-0.5 rounded">X-API-Key</code> (la chiave identifica già il centro)
            </p>
            {info.api_key ? (
              <div className="mt-5 flex items-center gap-2">
                <code className="flex-1 bg-[#FAFAFA] border border-black/10 rounded-2xl px-4 py-3 text-xs font-mono break-all" data-testid="api-key-value">
                  {info.api_key}
                </code>
                <button
                  data-testid="api-key-copy"
                  onClick={() => copy(info.api_key)}
                  className="inline-flex items-center gap-1.5 bg-[#1A1A1A] text-white rounded-full px-4 py-2.5 text-xs hover:bg-[#333]"
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  {copied ? "Copiata" : "Copia"}
                </button>
              </div>
            ) : (
              <div className="mt-5 text-sm text-[#737373] italic">Solo il titolare del centro può vedere la chiave.</div>
            )}
          </div>
        </div>
      </div>

      {/* Google Calendar */}
      <div className="mt-6 rounded-3xl border border-black/5 bg-white p-8" data-testid="gcal-card">
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-[#A78BFA] to-[#67E8F9] text-white flex items-center justify-center">
            <CalendarDays size={20} />
          </div>
          <div className="flex-1">
            <h2 className="font-serif text-2xl text-[#1A1A1A]">Google Calendar (sync agenda)</h2>
            <p className="mt-2 text-sm text-[#4A4A4A] font-light leading-relaxed">
              Collega l'account Google del salone: ogni appuntamento prenotato online finisce nel calendario condiviso col tuo CRM desktop.
            </p>
            {info.google_oauth_configured ? (
              <button
                data-testid="gcal-connect"
                disabled
                className="mt-5 inline-flex items-center gap-2 bg-[#1A1A1A] text-white rounded-full px-5 py-2.5 text-sm opacity-70 cursor-not-allowed"
              >
                {info.google_calendar_connected ? "Collegato ✓" : "Collega Google Calendar (presto)"}
              </button>
            ) : (
              <div className="mt-5 inline-flex items-start gap-2 bg-[#FEF3C7] border border-[#F59E0B]/30 text-[#92400E] rounded-2xl px-4 py-3 text-sm" data-testid="gcal-pending">
                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                <span>
                  In attesa di credenziali OAuth Google da parte del super-admin.<br />
                  Una volta configurate, sarà sufficiente premere "Collega" e autenticarsi col proprio account Google.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
